<template>
    <v-dialog
        v-model="internal_value"
        max-width="700"
        scrollable
    >
        <v-card>
            <v-card-title>{{ isNewTask ? 'Skapa ny uppgift' : 'Uppgiftsdetaljer' }}</v-card-title>
            <v-divider />
            <v-card-text
                class="pb-0"
                style="height: 400px"
            >
                <v-form ref="form">
                    <v-text-field
                        v-model="editedTask.headline"
                        label="Rubrik"
                        required
                        :rules="[v => (v && v.length > 0) || 'Rubrik är obligatoriskt']"
                    ></v-text-field>
                    <v-textarea
                        v-model="editedTask.description"
                        label="Beskrivning"
                        rows="3"
                        required
                        :rules="[v => (v && v.length > 0) || 'Beskrivning är obligatoriskt']"
                    ></v-textarea>

                    <!-- Deadline Date Picker -->
                    <v-menu
                        v-model="deadlineDatePickerMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="formattedDeadline"
                                label="Deadline"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                :clearable="isNewTask"
                                @click:clear="editedTask.deadline = null"
                                :rules="[
                                    v => !!v || 'Deadline är obligatoriskt',
                                    v => !v || !isPastDate(v) || 'Deadline kan inte vara ett passerat datum'
                                ]"
                                required
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="deadlineDate"
                            @input="deadlineDatePickerMenu = false"
                            :min="todayDate"
                        ></v-date-picker>
                    </v-menu>

                    <!-- Completed Date/Time Picker -->
                    <v-menu
                        v-if="!isNewTask"
                        v-model="completedDatePickerMenu"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="formattedCompleted"
                                label="Färdigställd"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                :clearable="!!editedTask.completed"
                                @click:clear="editedTask.completed = null"
                            ></v-text-field>
                        </template>
                        <v-card>
                            <v-date-picker
                                v-if="!showCompletedTimePicker"
                                v-model="completedDate"
                                @input="showCompletedTimePicker = true"
                            ></v-date-picker>
                            <v-time-picker
                                v-else
                                v-model="completedTime"
                                format="24hr"
                                @click:minute="completedDatePickerMenu = false; showCompletedTimePicker = false"
                            ></v-time-picker>
                        </v-card>
                    </v-menu>

                    <!-- Viewed by User (Read-only Datetime) -->
                    <v-text-field
                        v-if="!isNewTask"
                        :value="editedTask.viewed_by_user ? format_date_time(editedTask.viewed_by_user) : 'Användaren har inte sett uppgiften'"
                        label="Visad för användaren"
                        prepend-icon="mdi-eye"
                        readonly
                    ></v-text-field>
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-btn
                    v-if="!isNewTask"
                    color="error"
                    @click="deleteTask"
                >
                    Radera
                </v-btn>
                <v-spacer />
                <v-btn
                    @click="cancel"
                    color="grey"
                >
                    Avbryt
                </v-btn>
                <v-btn
                    color="primary"
                    @click="saveTask"
                >
                    Spara
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { api_mixin } from '@/mixins/services/api_mixin'
import { date_mixin } from '@/mixins/services/date_mixin'

export default {
    name: 'TaskDetails',
    props: {
        value: {
            type: Boolean,
        },
        task: {
            type: Object,
            default: () => ({}),
        },
        user_id: {
            type: String,
            required: true,
        },
    },
    mixins: [api_mixin, date_mixin],
    components: {},
    data() {
        return {
            editedTask: {},
            deadlineDatePickerMenu: false,
            completedDatePickerMenu: false,
            showCompletedTimePicker: false,
        }
    },
    computed: {
        internal_value: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            },
        },
        isNewTask() {
            return !this.task._id
        },
        formattedDeadline() {
            return this.editedTask.deadline ? this.format_date(this.editedTask.deadline) : ''
        },
        formattedCompleted() {
            return this.editedTask.completed ? this.format_date_time(this.editedTask.completed) : 'Användaren har inte slutfört uppgiften'
        },
        todayDate() {
            const today = new Date()
            const year = today.getFullYear()
            const month = String(today.getMonth() + 1).padStart(2, '0')
            const day = String(today.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },
        deadlineDate: {
            get() {
                return this.editedTask.deadline ? this.editedTask.deadline.split('T')[0] : null
            },
            set(val) {
                if (val) {
                    const newDate = new Date(val + 'T00:00:00Z');
                    if (this.editedTask.deadline) {
                        const oldDate = new Date(this.editedTask.deadline);
                        if (!isNaN(oldDate.getTime())) {
                            newDate.setUTCHours(oldDate.getUTCHours());
                            newDate.setUTCMinutes(oldDate.getUTCMinutes());
                            newDate.setUTCSeconds(oldDate.getUTCSeconds());
                        }
                    }
                    this.$set(this.editedTask, 'deadline', newDate.toISOString());
                } else {
                    this.$set(this.editedTask, 'deadline', null);
                }
            },
        },
        completedDate: {
            get() {
                return this.editedTask.completed ? this.editedTask.completed.split('T')[0] : null
            },
            set(val) {
                if (val) {
                    const newDate = new Date(val + 'T00:00:00Z');
                    if (this.editedTask.completed) {
                        const oldDate = new Date(this.editedTask.completed);
                        if (!isNaN(oldDate.getTime())) {
                            newDate.setUTCHours(oldDate.getUTCHours());
                            newDate.setUTCMinutes(oldDate.getUTCMinutes());
                            newDate.setUTCSeconds(oldDate.getUTCSeconds());
                        }
                    }
                    this.$set(this.editedTask, 'completed', newDate.toISOString());
                } else {
                    this.$set(this.editedTask, 'completed', null);
                }
            },
        },
        completedTime: {
            get() {
                if (!this.editedTask.completed) return null;
                const date = new Date(this.editedTask.completed);
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${hours}:${minutes}`;
            },
            set(val) {
                if (val && this.editedTask.completed) {
                    const [hours, minutes] = val.split(':');
                    const date = new Date(this.editedTask.completed);
                    date.setHours(parseInt(hours, 10));
                    date.setMinutes(parseInt(minutes, 10));
                    this.$set(this.editedTask, 'completed', date.toISOString());
                }
            },
        },
        ...mapGetters([]),
        ...mapState([]),
    },
    watch: {
        task: {
            handler(newTask) {
                const taskCopy = { ...newTask };
                if (taskCopy.deadline) {
                    taskCopy.deadline = new Date(taskCopy.deadline).toISOString();
                }
                if (taskCopy.completed) {
                    taskCopy.completed = new Date(taskCopy.completed).toISOString();
                }
                this.editedTask = taskCopy;

                if (this.isNewTask) {
                    this.$set(this.editedTask, 'user', this.user_id);
                }
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        isPastDate(dateString) {
            if (!dateString) return false
            const selectedDate = new Date(dateString)
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            selectedDate.setHours(0, 0, 0, 0)
            return selectedDate < today
        },
        async saveTask() {
            if (this.$refs.form.validate()) {
                try {
                    const payload = { ...this.editedTask };

                    if (payload.deadline) {
                        payload.deadline = new Date(payload.deadline).toUTCString();
                    }
                    if (payload.completed) {
                        payload.completed = new Date(payload.completed).toUTCString();
                    } else {
                        delete payload.completed;
                    }

                    if (this.isNewTask) {
                        await this.api_post({
                            url: 'x-u2work--tasks',
                            data: payload,
                        });
                    } else {
                        for (const key in payload) {
                            if (key.startsWith('_')) {
                                delete payload[key];
                            }
                        }
                        await this.api_put({
                            url: `x-u2work--tasks/${this.editedTask._id}`,
                            data: payload,
                            if_match: this.editedTask._etag,
                        });
                    }
                    this.$emit('task-saved');
                    this.internal_value = false;
                } catch (error) {
                    if (error.response && error.response.status === 412) {
                        await this.handleEtagMismatch();
                    } else {
                        console.error('Error saving task:', error);
                    }
                }
            }
        },
        async handleEtagMismatch() {
            const response = await this.api_get({
                url: `x-u2work--tasks/${this.editedTask._id}`,
                params: {
                    projection: {
                        headline: 1,
                        deadline: 1,
                        description: 1,
                        viewed_by_user: 1,
                        completed: 1,
                        user: 1,
                        _id: 1,
                        _etag: 1,
                        _created: 1,
                        _updated: 1,
                    },
                },
            });
            const reloadedTask = response.data;

            const editableFields = ['headline', 'description', 'deadline', 'completed'];
            const hasChanges = editableFields.some(field => {
                const currentValue = this.editedTask[field];
                const reloadedValue = reloadedTask[field];
                return JSON.stringify(currentValue) !== JSON.stringify(reloadedValue);
            });

            if (hasChanges) {
                alert('Uppgiften har ändrats av en annan användare. Granska ändringarna och spara igen.');
                if (reloadedTask.deadline) {
                    reloadedTask.deadline = new Date(reloadedTask.deadline).toISOString();
                }
                if (reloadedTask.completed) {
                    reloadedTask.completed = new Date(reloadedTask.completed).toISOString();
                }
                this.editedTask = reloadedTask;
            } else {
                this.internal_value = false;
            }
        },
        async deleteTask() {
            if (confirm('Radera?')) {
                try {
                    await this.api_delete({
                        url: `x-u2work--tasks/${this.editedTask._id}`,
                        if_match: this.editedTask._etag,
                    });
                    this.$emit('task-deleted');
                    this.internal_value = false;
                } catch (error) {
                    if (error.response && error.response.status === 412) {
                        await this.handleEtagMismatchForDelete();
                    } else {
                        console.error('Error deleting task:', error);
                    }
                }
            }
        },
        async handleEtagMismatchForDelete() {
            const response = await this.api_get({
                url: `x-u2work--tasks/${this.editedTask._id}`,
                params: {
                    projection: {
                        headline: 1,
                        deadline: 1,
                        description: 1,
                        viewed_by_user: 1,
                        completed: 1,
                        user: 1,
                        _id: 1,
                        _etag: 1,
                        _created: 1,
                        _updated: 1,
                    },
                },
            });
            const reloadedTask = response.data;

            alert('Uppgiften har ändrats av en annan användare. Datan har laddats om. Försök radera igen om du fortfarande vill ta bort uppgiften.');

            if (reloadedTask.deadline) {
                reloadedTask.deadline = new Date(reloadedTask.deadline).toISOString();
            }
            if (reloadedTask.completed) {
                reloadedTask.completed = new Date(reloadedTask.completed).toISOString();
            }
            this.editedTask = reloadedTask;
        },
        cancel() {
            this.internal_value = false;
        },
        ...mapActions([]),
    },
};
</script>

<style scoped lang="sass"></style>
